import type { ComponentType } from 'react';
import {
  Home20Regular,
  Bot20Regular,
  Flow20Regular,
  PlugConnected20Regular,
  SlideSettings20Regular,
  DataUsage20Regular,
  Settings20Regular,
} from '@fluentui/react-icons';
import type { AppPermissionLevel } from '@/hooks/useAppPermission';

export interface AppNavItem {
  id: string;
  label: string;
  icon: ComponentType;
  /** Relative path from the app layout (e.g., './workflows', './connections') */
  path: string;
  /** If true, NavLink uses exact (`end`) matching — required for `/` style absolute home links. */
  end?: boolean;
  /** Minimum permission level required to access this page. Defaults to 'Contributor'. */
  requiredPermission?: AppPermissionLevel;
}

export interface AppNavSection {
  id: string;
  /** When omitted, the section renders unlabelled and without a divider. */
  label?: string;
  items: AppNavItem[];
}

/**
 * App-scoped nav sections. Exposed as a function (not a module-level const)
 * because the Agents entry is gated on the `hostedAgents` feature flag, which
 * can be toggled at runtime (Settings → Experimental). Callers pass the current
 * flag value (from the reactive `useFeatureFlag` hook) so the sidebar updates
 * live on toggle instead of being frozen at module-init time.
 */
export function getAppNavSections(hostedAgentsEnabled: boolean): AppNavSection[] {
  return [
    {
      id: 'home',
      // No label — Home (and the link back to the parent project) sit at the
      // top as primary destinations. AppLayout injects the Project link at
      // runtime so it can use the live project name + tenant-aware href.
      items: [
        { id: 'home', label: 'Home', icon: Home20Regular, path: '/', end: true, requiredPermission: 'Reader' },
      ],
    },
    // Single app-scoped section. AppLayout overrides `label` with the
    // current app's name and supplies an icon at runtime — the static
    // "Application" label here is only the fallback used before the app loads.
    {
      id: 'application',
      label: 'Application',
      items: [
        { id: 'workflows', label: 'Workflows', icon: Flow20Regular, path: './workflows', requiredPermission: 'Reader' },
        // Hosted agents is a preview feature — only surface the Agents entry when the flag is on.
        ...(hostedAgentsEnabled
          ? [{ id: 'agents', label: 'Agents', icon: Bot20Regular, path: './agents', requiredPermission: 'Reader' as const }]
          : []),
        { id: 'connections', label: 'Connections', icon: PlugConnected20Regular, path: './connections', requiredPermission: 'Reader' },
        { id: 'parameters', label: 'Parameters', icon: SlideSettings20Regular, path: './parameters', requiredPermission: 'Reader' },
        { id: 'analytics', label: 'Analytics', icon: DataUsage20Regular, path: './analytics', requiredPermission: 'Reader' },
        { id: 'settings', label: 'Settings', icon: Settings20Regular, path: './settings', requiredPermission: 'Reader' },
      ],
    },
  ];
}

/**
 * Check whether a user's permission level meets the item's requirement.
 * Currently only distinguishes Reader (view-only) from Contributor/Author (full access).
 * If finer-grained levels are needed later, extend the hierarchy here.
 */
export function hasNavPermission(
  item: AppNavItem,
  userPermission: AppPermissionLevel,
): boolean {
  const required = item.requiredPermission ?? 'Contributor';
  if (!userPermission) return false;
  if (required === 'Reader') return true;
  return userPermission === 'Contributor' || userPermission === 'Owner';
}

/** Flat list derived from sections — backward compat. Call during render. */
export function getAppNavItems(hostedAgentsEnabled: boolean): AppNavItem[] {
  return getAppNavSections(hostedAgentsEnabled).flatMap((s) => s.items);
}
